import React from 'react'
import CIcon from '@coreui/icons-react'
import { cilOptions, cilArrowTop, cilArrowBottom } from '@coreui/icons'
import { CChartBar, CChartLine } from '@coreui/react-chartjs'

import {
  CCol,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CRow,
  CWidgetStatsA,
} from '@coreui/react'

// Formato universal: coma de miles (máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99").
const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)

const Definicion = ({ children }) => (
  <CDropdownItem disabled>{children}</CDropdownItem>
)

/**
 * Cuatro tarjetas superiores con el diseño oficial de CWidgetStatsA.
 * Las minigráficas usan SOLO series reales de GET /api/home/graficas
 * (no hay serie histórica de clientes en el endpoint actual, por eso cada
 * tarjeta muestra la serie mensual real de la métrica de negocio relacionada).
 */
// Variación vs mes anterior (BusinessVariation — backend /api/home/graficas
// Variacion). Texto con flecha y color SEMÁNTICO por tarjeta: en
// Clientes/Nuevos/Renovados subir es BUENO (verde ▲); en Vencidos subir es
// MALO (rojo ▲, la mora se expande) y bajar es bueno (verde ▼).
// color: color de la TARJETA (primary/success/info/danger) — la píldora lo
// usa; lo único que cambia con la tendencia es la FLECHA (icono CoreUI) con
// color semántico (verde bien, rojo mal, gris neutro — invertido en Vencidos).
const VariacionTexto = ({ variacion, invertir = false, color = 'primary' }) => {
  const pct = variacion?.pct
  if (pct === null || pct === undefined) return <small className="text-body-secondary">Sin comparación</small>
  // [RGM] 2026-10-01 (mejora aprobada — "cortada al mismo día"): cuando el mes
  // anterior no tuvo NADA y este mes SÍ, no se muestra +100% sino "nuevo".
  if (variacion?.anterior === 0 && (variacion?.actual ?? 0) > 0) {
    return (
      <span className="d-inline-flex align-items-center flex-wrap">
        <span
          className={`d-inline-flex align-items-center gap-1 px-1 py-0 rounded-pill bg-${color} text-white`}
          style={{ fontSize: '0.78rem', lineHeight: 1.4, marginRight: '0.25rem' }}
        >
          <span className="fw-normal">nuevo</span>
        </span>
        <span className="text-white" style={{ fontSize: '0.78rem' }}>Vs Mes Anterior</span>
      </span>
    )
  }
  // [RGM] 2026-09-24 (aclaración Rafael: "la flecha debe variar si aumenta o
  // baja"): ARRIBA solo si sube (>0), ABAJO si baja (<0) y SIN flecha cuando
  // es igual (0%) — antes el 0% mostraba flecha arriba (aumento falso).
  const Flecha = pct > 0 ? cilArrowTop : pct < 0 ? cilArrowBottom : null
  return (
    <span className="d-inline-flex align-items-center flex-wrap">
      {/* Píldora sólida del color de la tarjeta + texto, TODO en blanco */}
      <span
        className={`d-inline-flex align-items-center gap-1 px-1 py-0 rounded-pill bg-${color} text-white`}
        style={{ fontSize: '0.78rem', lineHeight: 1.4, marginRight: '0.25rem' }}
      >
        <span className="fw-normal">{pct.toFixed(1)}%</span>
        {Flecha && <CIcon icon={Flecha} size="sm" className="text-white" />}
      </span>
      <span className="text-white" style={{ fontSize: '0.78rem' }}>Vs Mes Anterior</span>
    </span>
  )
}

const WidgetsKpi = ({ kpi, graf }) => {
  const cobrado = graf?.cobrado ?? []
  const prestado = graf?.prestado ?? []
  const utilidad = graf?.utilidad ?? []
  const gastado = graf?.gastado ?? []
  // [RGM] 2026-09-24 (pedido Rafael): clientes CREADOS por mes — minichart de
  // la tarjeta Clientes (antes mostraba "cobrado", que no tiene relación).
  const clientesSerie = graf?.clientes ?? []
  // [RGM] 2026-09-24 (pedido Rafael): PRÉSTAMOS NUEVOS por mes — minichart de
  // la tarjeta Nuevos (antes mostraba "prestado", dinero sin relación). Así la
  // tarjeta queda coherente: valor/variación/minichart = préstamos.
  const nuevosSerie = graf?.nuevos ?? []
  // [RGM] 2026-09-24 (pedido Rafael): RENOVACIONES por mes y VENCIDOS por mes —
  // minicharts de las tarjetas Renovados y Vencidos (antes mostraban utilidad
  // y gastado, dinero sin relación).
  const renovadosSerie = graf?.renovados ?? []
  const vencidosSerie = graf?.vencidos ?? []

  return (
    <CRow>
      <CCol xs={12} sm={6} xl={3}>
        <CWidgetStatsA
          className="mb-4"
          color="primary"
          value={<><span>{fmt(kpi.clientesTotales)}</span> <VariacionTexto color="primary" variacion={graf?.variacion?.clientes} /></>}
          title="Clientes"
          action={
            <CDropdown alignment="end">
              <CDropdownToggle color="transparent" caret={false} className="p-0">
                <CIcon icon={cilOptions} className="text-white" />
              </CDropdownToggle>
              <CDropdownMenu>
                <Definicion>Total de clientes</Definicion>
                <Definicion>Minigráfica: clientes creados por mes</Definicion>
              </CDropdownMenu>
            </CDropdown>
          }
          chart={
            clientesSerie.length > 0 ? (
              <CChartLine
                className="mt-3 mx-3"
                style={{ height: '70px' }}
                data={{
                  labels: graf.meses,
                  datasets: [
                    {
                      label: 'Clientes',
                      backgroundColor: 'transparent',
                      borderColor: 'rgba(255,255,255,.55)',
                      data: clientesSerie,
                    },
                  ],
                }}
                options={{
                  plugins: { legend: { display: false }, datalabels: { display: false } },
                  maintainAspectRatio: false,
                  layout: { padding: { top: 8 } },
                  scales: {
                    x: {
                      border: { display: false },
                      grid: { display: false },
                      ticks: { display: false },
                    },
                    y: {
                      display: false,
                      grid: { display: false },
                      ticks: { display: false },
                      grace: '10%',
                    },
                  },
                  elements: {
                    line: { borderWidth: 1, tension: 0.4 },
                    point: { radius: 0, hitRadius: 10, hoverRadius: 4 },
                  },
                }}
              />
            ) : null
          }
        />
      </CCol>
      <CCol xs={12} sm={6} xl={3}>
        <CWidgetStatsA
          className="mb-4"
          color="success" // Verde — sincronizado con la serie "Nuevos" del chart Desempeño (dashboardColors.success)
          value={<><span>{fmt(kpi.clientesNuevos)}</span> <VariacionTexto color="success" variacion={graf?.variacion?.nuevos} /></>}
          title="Nuevos"
          action={
            <CDropdown alignment="end">
              <CDropdownToggle color="transparent" caret={false} className="p-0">
                <CIcon icon={cilOptions} className="text-white" />
              </CDropdownToggle>
              <CDropdownMenu>
                <Definicion>Definición Replica: préstamos con saldo &gt; 0</Definicion>
                <Definicion>Minigráfica: préstamos nuevos por mes</Definicion>
              </CDropdownMenu>
            </CDropdown>
          }
          chart={
            nuevosSerie.length > 0 ? (
              <CChartLine
                className="mt-3 mx-3"
                style={{ height: '70px' }}
                data={{
                  labels: graf.meses,
                  datasets: [
                    {
                      label: 'Préstamos nuevos',
                      backgroundColor: 'transparent',
                      borderColor: 'rgba(255,255,255,.55)',
                      data: nuevosSerie,
                    },
                  ],
                }}
                options={{
                  plugins: { legend: { display: false }, datalabels: { display: false } },
                  maintainAspectRatio: false,
                  layout: { padding: { top: 8 } },
                  scales: {
                    x: {
                      border: { display: false },
                      grid: { display: false },
                      ticks: { display: false },
                    },
                    y: {
                      display: false,
                      grid: { display: false },
                      ticks: { display: false },
                      grace: '10%',
                    },
                  },
                  elements: {
                    line: { borderWidth: 1 },
                    point: { radius: 3, hitRadius: 10, hoverRadius: 4 },
                  },
                }}
              />
            ) : null
          }
        />
      </CCol>
      <CCol xs={12} sm={6} xl={3}>
        <CWidgetStatsA
          className="mb-4"
          color="info" // Azul claro — sincronizado con la serie "Renovaciones" del chart Desempeño (dashboardColors.info, $info SCSS = #078bcd)
          value={<><span>{fmt(kpi.clientesRenovados)}</span> <VariacionTexto color="info" variacion={graf?.variacion?.renovados} /></>}
          title="Renovados"
          action={
            <CDropdown alignment="end">
              <CDropdownToggle color="transparent" caret={false} className="p-0">
                <CIcon icon={cilOptions} className="text-white" />
              </CDropdownToggle>
              <CDropdownMenu>
                <Definicion>Definición Replica: tipo de préstamo Renovación</Definicion>
                <Definicion>Minigráfica: renovaciones por mes</Definicion>
              </CDropdownMenu>
            </CDropdown>
          }
          chart={
            renovadosSerie.length > 0 ? (
              <CChartLine
                className="mt-3 mx-3"
                style={{ height: '70px' }}
                data={{
                  labels: graf.meses,
                  datasets: [
                    {
                      label: 'Renovaciones',
                      backgroundColor: 'transparent',
                      borderColor: 'rgba(255,255,255,.55)',
                      data: renovadosSerie,
                    },
                  ],
                }}
                options={{
                  plugins: { legend: { display: false }, datalabels: { display: false } },
                  maintainAspectRatio: false,
                  layout: { padding: { top: 8 } },
                  scales: {
                    x: {
                      border: { display: false },
                      grid: { display: false },
                      ticks: { display: false },
                    },
                    y: {
                      display: false,
                      grid: { display: false },
                      ticks: { display: false },
                      grace: '10%',
                    },
                  },
                  elements: {
                    line: { borderWidth: 2, tension: 0.4 },
                    point: { radius: 3, hitRadius: 10, hoverRadius: 4 },
                  },
                }}
              />
            ) : null
          }
        />
      </CCol>
      <CCol xs={12} sm={6} xl={3}>
        <CWidgetStatsA
          className="mb-4"
          color="danger"
          value={<><span>{fmt(kpi.clientesEnRiesgo)}</span> <VariacionTexto color="danger" variacion={graf?.variacion?.vencidos} invertir /></>}
          title="Vencidos"
          action={
            <CDropdown alignment="end">
              <CDropdownToggle color="transparent" caret={false} className="p-0">
                <CIcon icon={cilOptions} className="text-white" />
              </CDropdownToggle>
              <CDropdownMenu>
                <Definicion>Saldo real &gt; 0 y fecha término pasada</Definicion>
                <Definicion>Minigráfica: vencidos por mes</Definicion>
              </CDropdownMenu>
            </CDropdown>
          }
          chart={
            vencidosSerie.length > 0 ? (
              // [RGM] 2026-09-24 (pedido Rafael): Vencidos con BARRAS
              // VERTICALES (como estaba), con la serie de vencidos/mes.
              <CChartBar
                className="mt-3 mx-3"
                style={{ height: '70px' }}
                data={{
                  labels: graf.meses,
                  datasets: [
                    {
                      label: 'Vencidos',
                      backgroundColor: 'rgba(255,255,255,.55)',
                      data: vencidosSerie,
                      // [RGM] 2026-10-01 (pedido Rafael): barras finas como el
                      // original de CoreUI — luego "un poco más anchas" (8).
                      maxBarThickness: 8,
                      barPercentage: 0.5,
                      categoryPercentage: 0.6,
                    },
                  ],
                }}
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false }, datalabels: { display: false } },
                  layout: { padding: { top: 8 } },
                  scales: {
                    x: {
                      grid: { display: false, drawTicks: false },
                      ticks: { display: false },
                    },
                    y: {
                      display: false,
                      border: { display: false },
                      grid: { display: false, drawTicks: false },
                      ticks: { display: false },
                      beginAtZero: true,
                      grace: '10%',
                    },
                  },
                }}
              />
            ) : null
          }
        />
      </CCol>
    </CRow>
  )
}

export default WidgetsKpi
